/* eslint-disable react-hooks/exhaustive-deps */
import { DependencyList, useCallback, useRef, useState } from 'react'
import * as Sentry from "@sentry/react";

export type AsyncActionResult<T> =
  {
    loading: false,
    error?: undefined,
    data?: undefined
  } | {
    loading: true,
    error?: undefined,
    data?: undefined
  } | {
    loading: false,
    error: Error,
    data?: undefined
  } | {
    loading: false,
    error?: undefined,
    data: T
  }

export function useAsyncAction<T extends (...args: any[]) => Promise<any>>(action: T, deps?: DependencyList) {
  const [result, setResult] = useState<AsyncActionResult<Awaited<ReturnType<T>>>>({
    loading: false,
  })
  const invocationCount = useRef<number>(0)

  const wrapped: T = useCallback((...args: any[]) => {
    // restart the sequence
    setResult({
      loading: true,
    })
    const invocationId = invocationCount.current + 1 % (Number.MAX_SAFE_INTEGER / 2)
    invocationCount.current = invocationId

    // run the action
    const promise = action(...args)
    // handle results — the chained promise is intentionally swallowed via
    // .catch so it doesn't leak as an unhandled rejection. The original
    // `promise` is still returned to the caller (and still rejects), so
    // callers who `await wrapped()` continue to see the error.
    promise.then(
        (data) => {
          if (invocationId !== invocationCount.current) {
            // this is a stale invocation, ignore it
            return
          }

          // success!
          setResult({
            loading: false,
            data: data as any
          })
          return data
        },
        (err) => {
          console.log('error!', err, invocationId, invocationCount.current)
          if (invocationId !== invocationCount.current) {
            // this is a stale invocation, ignore it
            return
          }
          Sentry.captureException(err);

          setResult({
            loading: false,
            error: err
          })
          throw err
        }).catch(() => { /* see comment above */ })

    // return the promise so the invoker can also handle the result
    return promise
  }, deps || []) as T

  return [
    result,
    wrapped,
    reset
  ] as const

  function reset() {
    setResult({
      loading: false,
    })
  }
}
